import { useTrialApplication } from '@components/hooks/TrialApplicationQuestionnaireProvider/index.client'
import { HideContentFromServerRender } from '@components/primitives/HideContentFromServerRender'
import { Text } from '@components/primitives/Text'
import getEligibilityCriteriaFieldKey from '@lib/questionnaire/trialApplication/eligibilityCriteria/getEligibilityCriteriaFieldKey'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { TrackingSources } from '@lib/tracking/TrackingSources'
import isEmptyString from '@lib/utilities/text/isEmptyString'
import { findAll } from 'highlight-words-core'
import { Fragment, useState } from 'react'
import { type Eligibility, IsEligiblePicker } from './IsEligiblePicker'
import { ShowSummaryClickableText } from './ShowSummaryClickableText'

export const ColorfulEligibilityCriterion = ({
  criterion,
  defaultValue,
  slug,
}: {
  criterion: {
    assertiveSummary?: string | null
    criterion: string
    id?: number | null
    isInclusion?: boolean
    summaryHighlightedTerms?: string[] | null
  }
  defaultValue?: Eligibility
  slug?: string | null
}) => {
  const { persistTrialApplicationQuestionnaireData } = useTrialApplication()
  const [isOriginal, setIsOriginal] = useState(false)
  const hasSummary =
    !!criterion.assertiveSummary &&
    !isEmptyString(criterion.assertiveSummary) &&
    criterion.assertiveSummary !== criterion.criterion
  const criterionValue =
    hasSummary && !isOriginal ? criterion.assertiveSummary : criterion.criterion

  const questionnaireAttribute = criterion.id
    ? getEligibilityCriteriaFieldKey(
        criterion.id,
        criterion.isInclusion ?? true,
      )
    : undefined

  const onUpdate = (val?: 'Yes' | 'Unsure' | 'No') => {
    trackEvent(TrackingEvents.ELIGIBILITY_CHECKBOX_CLICKED, {
      source: TrackingSources.LISTING,
      val,
    })
    questionnaireAttribute && slug
      ? persistTrialApplicationQuestionnaireData({
          data: { [questionnaireAttribute]: val },
          slug,
        })
      : undefined
  }

  return (
    <HideContentFromServerRender hideContent={!hasSummary}>
      <div className='flex flex-wrap-reverse gap-4 pb-4 md:flex-nowrap'>
        <div className='w-28'>
          <IsEligiblePicker
            isInclusion={criterion.isInclusion}
            setValue={onUpdate}
            value={defaultValue}
          />
        </div>
        <div className='w-full md:w-5/6'>
          <HighlightedText
            highlights={criterion.summaryHighlightedTerms}
            text={criterionValue}
          />
          {hasSummary && (
            <ShowSummaryClickableText
              isOriginal={isOriginal}
              setIsOriginal={setIsOriginal}
            />
          )}
        </div>
      </div>
    </HideContentFromServerRender>
  )
}

const HighlightedText = ({
  highlights,
  text,
}: {
  highlights?: string[] | null
  text?: string | null
}) => {
  if (!text) return null
  if (!highlights) {
    return (
      <Text
        className='line-clamp-2 text-neutral900'
        styleName='p-small'
        value={text}
      />
    )
  }

  const chunks = findAll({
    autoEscape: true,
    searchWords: highlights,
    textToHighlight: text,
  })

  const highlightedText = chunks.map((chunk, index) => {
    const { end, highlight, start } = chunk
    const val = text.slice(start, end)
    if (highlight) {
      return (
        <span className='bg-primary50 font-semibold' key={index}>
          {val}
        </span>
      )
    } else {
      return <Fragment key={index}>{val}</Fragment>
    }
  })

  return (
    <Text
      className='text-neutral900'
      styleName='p-small'
      value={highlightedText}
    />
  )
}
